'use client';

import { API_ROUTES, useApiQuery } from '@shared/api-utils';
import type { LotteryName } from '../../../lottery-purchases/types';
import type { DrawingsResponse, ResultsByRangeQueryParams } from '../../types';

type UseResultsByRangeOptions = {
  params: ResultsByRangeQueryParams;
  enabled?: boolean;
};

function getResultsByRangePath(lotteryName: LotteryName): string {
  return API_ROUTES.RESULTS.BY_RANGE.replace(':lotteryName', lotteryName);
}

function toResultsByRangeApiParams(params: ResultsByRangeQueryParams): Record<string, string | number> | undefined {
  if (!params.from || !params.to) return undefined;

  const apiParams: Record<string, string | number> = {
    from: params.from,
    to: params.to,
  };

  if (params.offset !== undefined && params.offset !== '') apiParams.offset = params.offset;
  if (params.limit !== undefined && params.limit !== '') apiParams.limit = params.limit;

  return apiParams;
}

/**
 * Fetches lottery results whose drawing date falls within `[from, to)` (LRS v2 `getResultsByRange`).
 * Ordered by drawing date desc. When `limit` is omitted the backend uses the per-lottery-type maximum.
 */
function useResultsByRange(lotteryName: LotteryName, options: UseResultsByRangeOptions) {
  const { params, enabled = true } = options;
  const path = getResultsByRangePath(lotteryName);
  const apiParams = toResultsByRangeApiParams(params);

  return useApiQuery<DrawingsResponse>(path, {
    params: apiParams,
    queryOptions: {
      enabled: Boolean(lotteryName) && Boolean(apiParams) && enabled,
      placeholderData: previousData => previousData,
    },
  });
}

export { getResultsByRangePath, toResultsByRangeApiParams, useResultsByRange };
export type { UseResultsByRangeOptions };
